/**
 * CSV export utilities for the admin portal.
 */

import type { GroupedSuggestion } from '@/types/admin'

/**
 * Escape a value for inclusion in a CSV cell.
 *
 * Per RFC 4180:
 * - Wrap the value in double-quotes if it contains a comma, double-quote, or newline.
 * - Escape internal double-quotes by doubling them ("").
 */
function escapeCsvValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  // If the string contains a comma, double-quote, or newline, wrap it in quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Convert an array of row arrays to a CSV string.
 */
function rowsToCsv(rows: (string | number | null | undefined)[][]): string {
  return rows.map((row) => row.map(escapeCsvValue).join(',')).join('\r\n')
}

/**
 * Trigger a browser download of a CSV string.
 *
 * Creates a temporary Blob URL, clicks it, then revokes it.
 */
function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Generate a datestamped filename for the CSV export.
 */
function buildFilename(): string {
  const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
  return `piece-suggestions-${today}.csv`
}

/**
 * Export a list of grouped suggestions as a CSV file and trigger a browser download.
 *
 * Columns:
 *   Title, Composer, Last Performed, Number of Performances, Suggested By
 *
 * - "Suggested By" lists member names (or emails as fallback) joined by "; ".
 * - Performance columns are blank for pieces not found in the concert archive.
 */
export function exportSuggestionsAsCsv(suggestions: GroupedSuggestion[]): void {
  const headers = ['Title', 'Composer', 'Last Performed', 'Number of Performances', 'Suggested By']

  const dataRows = suggestions.map((s) => {
    const lastPerformed = s.performance_history?.last_performed ?? ''
    const performanceCount =
      s.performance_history != null ? s.performance_history.performance_count : ''

    const suggestedBy = s.suggesters
      .map((suggester) => suggester.user_name ?? suggester.user_email ?? suggester.user_id)
      .join('; ')

    return [s.title, s.composer, lastPerformed, performanceCount, suggestedBy]
  })

  const csv = rowsToCsv([headers, ...dataRows])
  downloadCsv(csv, buildFilename())
}
